import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Typography, Spin, Empty, Button, Switch, Tooltip } from 'antd';
import { MenuOutlined, PrinterOutlined, ReloadOutlined, ShopOutlined, ClockCircleOutlined, SyncOutlined } from '@ant-design/icons';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { getAllSKUsGroupedByBooth, SKU } from '@festpix/services';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;

const AUTO_UPDATE_INTERVAL = 5 * 60 * 1000; // 5 minutes

interface BoothWithSKUs {
  boothName: string;
  skus: SKU[];
}

export default function MenuViewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoUpdate, setAutoUpdate] = useState(false);
  const [boothsData, setBoothsData] = useState<Map<string, BoothWithSKUs>>(new Map());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await getAllSKUsGroupedByBooth();
      setBoothsData(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading menu data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadData();
      }
    });
    return unsubscribe;
  }, [navigate, loadData]);

  // Auto-update effect
  useEffect(() => {
    if (autoUpdate) {
      intervalRef.current = setInterval(() => {
        loadData(true);
      }, AUTO_UPDATE_INTERVAL);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoUpdate, loadData]);

  const handleRefresh = () => {
    loadData(true);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handlePrint = () => {
    window.print();
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  const boothEntries = Array.from(boothsData.entries());

  return (
    <Layout>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            .print-content {
              padding: 10px;
            }
            .menu-card {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }
        `}
      </style>

      <div
        className="no-print"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <MenuOutlined style={{ marginRight: 12 }} />
          Cardápio Geral
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {lastUpdated && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Atualizado às {formatTime(lastUpdated)}
            </Text>
          )}
          <Tooltip title="Atualiza automaticamente a cada 5 minutos">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SyncOutlined spin={autoUpdate} style={{ color: autoUpdate ? colors.success : colors.textSecondary }} />
              <Text style={{ fontSize: 13 }}>Auto</Text>
              <Switch
                size="small"
                checked={autoUpdate}
                onChange={setAutoUpdate}
              />
            </div>
          </Tooltip>
          <Button
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Atualizar
          </Button>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            Imprimir
          </Button>
        </div>
      </div>

      <div className="print-content">
        {boothEntries.length === 0 ? (
          <div style={{ background: colors.cardBackground, padding: 40, borderRadius: 8, textAlign: 'center' }}>
            <Empty
              description="Nenhum SKU cadastrado"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={() => navigate('/admin/booths')}>
                Ir para Estandes
              </Button>
            </Empty>
          </div>
        ) : (
          <div
            style={{
              background: colors.cardBackground,
              borderRadius: 8,
              padding: '12px 16px',
            }}
          >
            {boothEntries.map(([boothId, { boothName, skus }], index) => (
              <div key={boothId} className="menu-card">
                {/* Compact booth header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 0 6px 0',
                    borderTop: index > 0 ? `1px solid ${colors.border}` : 'none',
                    marginTop: index > 0 ? 8 : 0,
                  }}
                >
                  <ShopOutlined style={{ color: colors.primary, fontSize: 14 }} />
                  <Text strong style={{ fontSize: 14, color: colors.primaryDark }}>
                    {boothName}
                  </Text>
                </div>

                {skus.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 13, paddingLeft: 22 }}>
                    Nenhum produto
                  </Text>
                ) : (
                  <div style={{ paddingLeft: 22 }}>
                    {skus.map((sku) => (
                      <div
                        key={sku.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            textDecoration: sku.quantity === 0 ? 'line-through' : 'none',
                            color: sku.quantity === 0 ? colors.textDisabled : colors.text,
                          }}
                        >
                          {sku.name}
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              borderBottom: `1px dotted ${colors.borderLight}`,
                              flex: 1,
                              minWidth: 20,
                            }}
                          />
                          <Text
                            strong
                            style={{
                              fontSize: 14,
                              color: sku.quantity === 0 ? colors.textDisabled : colors.success,
                              textDecoration: sku.quantity === 0 ? 'line-through' : 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatPrice(sku.price)}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
